export type SyncEntitlementStatus = 'active' | 'inactive';

export type SyncEntitlement = {
  status: SyncEntitlementStatus;
  owned: boolean;
  capabilities: Array<'sync' | 'remove_ads'>;
  source?: string;
};

export type AccountSession = {
  token: string;
  expiresAt: string;
  entitlement: SyncEntitlement;
};

type AppleExchangeResponse = {
  account: { id: string };
  entitlement: SyncEntitlement;
  session: { token: string; expiresAt: string };
};

const ACCOUNT_API_URL = (import.meta.env.VITE_ACCOUNT_API_URL as string | undefined)?.replace(/\/$/, '');
const TEST_ENTITLEMENT = import.meta.env.DEV
  ? import.meta.env.VITE_TEST_SYNC_ENTITLEMENT as SyncEntitlementStatus | undefined
  : undefined;

const errorMessage = async (response: Response) => {
  const payload = await response.json().catch(() => null) as { error?: { message?: string } } | null;
  return payload?.error?.message;
};

export const exchangeAppleIdentity = async (identityToken: string): Promise<AccountSession> => {
  if (!ACCOUNT_API_URL) {
    throw new Error('계정 동기화 서버가 아직 연결되지 않았습니다. 로그인 없이 기기에 저장할 수 있습니다.');
  }
  const response = await fetch(`${ACCOUNT_API_URL}/v1/auth/apple`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(TEST_ENTITLEMENT ? { 'x-daykeeper-test-entitlement': TEST_ENTITLEMENT } : {}),
    },
    body: JSON.stringify({ identityToken }),
  });
  if (!response.ok) {
    throw new Error((await errorMessage(response)) ?? 'Apple 계정을 확인하지 못했습니다. 다시 시도해 주세요.');
  }
  const payload = await response.json() as AppleExchangeResponse;
  return {
    token: payload.session.token,
    expiresAt: payload.session.expiresAt,
    entitlement: payload.entitlement,
  };
};

export const fetchAccountEntitlement = async (session: AccountSession): Promise<AccountSession> => {
  if (!ACCOUNT_API_URL) throw new Error('계정 동기화 서버가 아직 연결되지 않았습니다.');
  const response = await fetch(`${ACCOUNT_API_URL}/v1/session`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${session.token}` },
  });
  if (!response.ok) {
    throw new Error((await errorMessage(response)) ?? '계정 상태를 갱신하지 못했습니다.');
  }
  const payload = await response.json() as { entitlement: SyncEntitlement };
  return { ...session, entitlement: payload.entitlement };
};

const mutateAccountSession = async (session: AccountSession, path: '/v1/session' | '/v1/account') => {
  if (!ACCOUNT_API_URL) throw new Error('계정 동기화 서버가 아직 연결되지 않았습니다.');
  const response = await fetch(`${ACCOUNT_API_URL}${path}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', Authorization: `Bearer ${session.token}` },
  });
  if (!response.ok) {
    throw new Error((await errorMessage(response)) ?? '계정 요청을 완료하지 못했습니다.');
  }
};

export const revokeAccountSession = (session: AccountSession) => mutateAccountSession(session, '/v1/session');
export const deleteAccountData = (session: AccountSession) => mutateAccountSession(session, '/v1/account');

export const accountApiBaseUrl = ACCOUNT_API_URL;
